import { useCallback, useEffect, useState } from "react";

/**
 * Where Tafo-beheer is (ADR 0012): in production on its own address (beheer.<domein>), in development and
 * the demo under /beheer of the till. Every screen has its own address, so the browser's back button, a
 * bookmark or a link in a mail opens the same screen.
 */

export const STEPS = ["contact", "resto", "hours", "zones", "menu", "kassa", "staff", "plan", "akkoord"] as const;
export type Step = (typeof STEPS)[number];

export const SHOP_TABS = [
  "overzicht",
  "menu",
  "instellingen",
  "toestellen",
  "facturen",
  "dossier",
  "codes",
  // the shop's Meldingen, for Tafo only (Furkan, 15/10/2026)
  "meldingen",
  // Mymenu.be, for Tafo only (Furkan, 15/10/2026)
  "koppelingen",
  // the shop's own print as its folder (ADR 0052; Furkan, 22/10/2026)
  "drukwerk",
] as const;
export type ShopTab = (typeof SHOP_TABS)[number];

/**
 * The pages with an address of their own, in the order of the prototype's menu (TB_NAV). The menu on the
 * left no longer shows them all: Aanvragen and Betalingen & facturen are tabs of Zaken & abonnementen
 * (Furkan, 06/10/2026: "alles onder zaken en abonnementen"); their addresses stay.
 */
export const SECTIONS = [
  "overzicht",
  "aanvragen",
  "zaken",
  "betalingen",
  "archief",
  "toestellen",
  "online",
  "menuprompt",
  "allergenen",
  "voorwaarden",
  "logboek",
  "instellingen",
] as const;
export type Section = (typeof SECTIONS)[number];

export type Route =
  | { page: Exclude<Section, "aanvragen" | "zaken"> }
  | { page: "aanvragen" }
  | { page: "aanvraag"; id: string; step: Step | null }
  | { page: "zaken" }
  | { page: "zaak"; id: string; tab: ShopTab }
  | { page: "uitnodiging"; token: string };

/** The tabs of Zaken & abonnementen, each a page with its own address. */
export const SHOPS_TABS = ["zaken", "aanvragen", "betalingen"] as const;
export type ShopsTab = (typeof SHOPS_TABS)[number];

/** The item of the menu a page belongs to: a shop, a request and the payments to Zaken & abonnementen. */
export function sectionOf(route: Route): Section | null {
  if (route.page === "aanvraag" || route.page === "aanvragen" || route.page === "betalingen") return "zaken";
  if (route.page === "zaak") return "zaken";
  if (route.page === "uitnodiging") return null;
  return route.page;
}

/** On beheer.<domein> the panel is the whole site; elsewhere it lives under /beheer. */
export function beheerBase(location: Pick<Location, "hostname"> = window.location): string {
  return location.hostname.startsWith("beheer.") ? "" : "/beheer";
}

/** Whether this page is Tafo-beheer (main.tsx decides which app to load). */
export function isBeheer(location: Pick<Location, "hostname" | "pathname"> = window.location): boolean {
  return location.hostname.startsWith("beheer.") || /^\/beheer(\/|$)/.test(location.pathname);
}

/**
 * The till's address seen from Tafo-beheer: beheer.tafo.be → tafo.be; in development the same address.
 * The support window (🛟 Inloggen als zaak) opens the till there.
 */
export function tillOrigin(location: Pick<Location, "origin" | "hostname"> = window.location): string {
  return location.hostname.startsWith("beheer.") ? location.origin.replace("//beheer.", "//") : location.origin;
}

const isStep = (value: string | undefined): value is Step => (STEPS as readonly string[]).includes(value ?? "");
const isTab = (value: string | undefined): value is ShopTab => (SHOP_TABS as readonly string[]).includes(value ?? "");
const isSection = (value: string | undefined): value is Section =>
  (SECTIONS as readonly string[]).includes(value ?? "");

export function parseRoute(pathname: string, base = beheerBase()): Route {
  const rest = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  const parts = rest.split("/").filter(Boolean).map(decodeURIComponent);
  const [first, second, third] = parts;
  if (first === "uitnodiging" && second) return { page: "uitnodiging", token: second };
  if (first === "aanvragen" && second && /^APP-[0-9A-F]{6}$/.test(second)) {
    return { page: "aanvraag", id: second, step: isStep(third) ? third : null };
  }
  if (first === "zaken" && second && /^[0-9a-f-]{36}$/.test(second)) {
    return { page: "zaak", id: second, tab: isTab(third) ? third : "overzicht" };
  }
  if (isSection(first)) return { page: first };
  return { page: "overzicht" };
}

export function routePath(route: Route, base = beheerBase()): string {
  const path = (() => {
    switch (route.page) {
      case "overzicht":
        return "/";
      case "aanvraag":
        return `/aanvragen/${route.id}${route.step ? `/${route.step}` : ""}`;
      case "zaak":
        return `/zaken/${route.id}${route.tab === "overzicht" ? "" : `/${route.tab}`}`;
      case "uitnodiging":
        return `/uitnodiging/${encodeURIComponent(route.token)}`;
      default:
        return `/${route.page}`;
    }
  })();
  return base === "" ? path : path === "/" ? base : base + path;
}

/** The route of this page; `go` changes it (a new entry in the history, or the same one with replace). */
export function useRoute(): [Route, (route: Route, options?: { replace?: boolean }) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const onPop = () => {
      setRoute(parseRoute(window.location.pathname));
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, []);
  const go = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = routePath(next);
    if (path !== window.location.pathname) {
      if (options?.replace === true) window.history.replaceState(null, "", path);
      else window.history.pushState(null, "", path);
    }
    setRoute(next);
    window.scrollTo(0, 0);
  }, []);
  return [route, go];
}
